<script setup>
import { computed, ref, onBeforeMount } from "vue";
import EventCard from "@/components/EventCard.vue";
import { useMainStore } from "../../../../stores/mainStore";
import { getAgesString } from "../../../../libs/ages.helper.js";
import { ACTIVE_FEATURES, getFireBaseFeatureValue } from
    "../../../../components/methods/getFeatureValue";

const isSellingTicketFeatureActive = ref(false);

const convertTime12to24 = (time12h) => {
    const [time, modifier] = time12h.split(" ");

    // eslint-disable-next-line prefer-const
    let [hours, minutes] = time.split(":");

    if (hours === "12") {
        hours = "00";
    }

    if (modifier === "PM") {
        hours = parseInt(hours, 10) + 12;
    }

    return `${hours}:${minutes}`;
};

function formatStrToSeconds(timeString) {
    const clearString = convertTime12to24(timeString);
    const minutesSecondsArray = clearString.split(":");
    const secondsInMinute = 60;
    return Number.parseInt(minutesSecondsArray[0] * secondsInMinute + minutesSecondsArray[1], 10);
}

function sortSessions(a, b) {
    const title1 = a.activity.title;
    const title2 = b.activity.title;
    const start1 = formatStrToSeconds(a.time_start);
    const start2 = formatStrToSeconds(b.time_start);

    const end1 = formatStrToSeconds(a.time_finish);
    const end2 = formatStrToSeconds(b.time_finish);

    if (start1 !== start2) {
        return start1 > start2 || end1 > end2 ? 1 : -1;
    }
    if (end1 !== end2) {
        return start1 > start2 || end1 > end2 ? 1 : -1;
    }
    return title1.localeCompare(title2);
}

const props = defineProps({
    sessions: {
        type: Array,
        default: () => [],
    },
    selectedDay: {
        type: String,
        default: "mon",
    },
    venueId: {
        type: String,
        default: "",
    },
    termId: {
        type: String,
        default: "",
    },
    repetition: {
        type: String,
        default: "",
    },
});

const mainStore = useMainStore();

const getFilteredSessionsList = computed(() => {
    if (props.repetition === "weekly") {
        const dayFilteredSessions = props.sessions.filter(
            ({ day }) => (
                day === props.selectedDay
                || day?.substring(0, 3) === props.selectedDay
            ),
        );

        // return dayFilteredSessions;
        return dayFilteredSessions.sort(sortSessions);
    }

    const { sessions } = props;
    // return sessions;
    return sessions.sort(sortSessions);
});

function getAges(session) {
    if (mainStore.agesForActivities[session.activity_id]) {
        return getAgesString(mainStore.agesForActivities[session.activity_id]);
    }

    return [];
}

onBeforeMount(async () => {
    isSellingTicketFeatureActive.value = await getFireBaseFeatureValue(
        ACTIVE_FEATURES.SELLING_TICKET,
    );
});
</script>

<template>
    <div
        v-for="session in getFilteredSessionsList"
        :key="session.timetable_id"
        :data-test="`session-${session.timetable_id}`"
    >
        <EventCard
            :title="session.name || session.activity.title"
            :session="session"
            :ages="getAges(session)"
            :image="mainStore.IMAGE_BASE_LINK + session.activity.listing_image_name"
            route-name="Advanced event series"
            :extended="true"
            :route-params="{
                venueId,
                actId: session.activity_id,
                termId,
                sessionId: session.timetable_id,
                weekday: repetition === 'weekly' ? selectedDay : '',
            }"
            :isSellingTicketFeatureActive="isSellingTicketFeatureActive"
        />
    </div>
    <div
        v-if="!getFilteredSessionsList.length"
        :data-test="`session-empty`"
        class="empty-session-list"
    >
        <span v-if="repetition === 'weekly'">
            You haven't created any weekly sessions on this day yet
        </span>
        <span v-if="repetition === 'daily'"> You haven't created any daily sessions yet </span>
    </div>
</template>
<style scoped>
.empty-session-list {
    background: #ffffff;
    box-shadow: 0px 0px 8px rgba(0, 0, 0, 0.15);
    border-radius: 0.9375rem;
    padding: 1rem;
}
</style>
